import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'hooks/useTranslations';
import { useAlert } from 'models/alertContext';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { ActionTypes } from 'state/actions/alert';
import { Table } from 'components/molecules/Table';
import { renderRowColor, renderRowColorHover } from 'components/molecules/Table/helpers';
import { mapLEAddressToDataSource, SelectLocationTableColumns } from './helpers';
import { parseDictionary } from 'helpers/dictionary';
import { useDictionary } from 'models/dictionaryContext';
import { useLang } from 'models/langContext';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { isListNotEmpty } from 'utils/listUtils';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { CardList } from 'components/molecules/CardList';
import { ITableDataSource } from 'models/table';
import { SelectLocationAddressTableCard } from './AddressTableCard';
import { Grid } from '@mui/material';

interface ISelectLocationTransactionPage {
  searchAddressesFor: string | null;
  canSelectOnlyExistingAddress: boolean;
  show: boolean;
  addressId: string | null;
  setValue: any;
}

const SelectLocationTransactionPage = ({
  searchAddressesFor,
  canSelectOnlyExistingAddress,
  show,
  addressId,
  setValue,
}: ISelectLocationTransactionPage) => {
  const { isDesktop } = useThemeBreakpoint();
  const { id = '' } = useParams<{ id: string }>();
  const { t } = useTranslations();
  const { clearAlert } = useAlert();
  const { region, country, addressType, getRegion, getCountry, getAddressType } =
    useDictionary();
  const { selectedLanguage } = useLang();
  const { getLegalEntityStorageAddressesData, legalEntityStorageAddress } = useLegalEntities();
  const [selectedAddress, setSelectedAddress] = useState<ITableDataSource | null>(null);

  useEffect(() => {
    if (searchAddressesFor) {
      getLegalEntityStorageAddressesData(searchAddressesFor);
    }
  }, [searchAddressesFor]);

  useEffect(() => {
    if (!show) {
      setValue('addressId', null);
      setValue('latitude', null);
      setValue('longitude', null);
      setValue('address', null);

      setValue('city', null);
      setValue('countryUniqueId', null);
    }
  }, [show]);

  useEffect(() => {
    getRegion();
    getAddressType();
    getCountry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (selectedAddress !== null) {
      if (selectedAddress) {
        setValue('latitude', selectedAddress.latitude);
        setValue('longitude', selectedAddress.longitude);
        setValue('addressId', selectedAddress.id.toString());
        setValue('address', selectedAddress.line1);
        setValue('city', selectedAddress.city);
        setValue('countryUniqueId', selectedAddress.countryUniqueId);
      }
    }
  }, [selectedAddress]);

  const handleCheck = (checkedAddress: ITableDataSource) => {
    setSelectedAddress(checkedAddress);
    clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    clearAlert(ActionTypes.CLEAR_API_ALERT);
  };

  if (show) {
    return (
      <Grid container>
        <Grid size={{ xs: 12 }}>
          {isListNotEmpty(legalEntityStorageAddress) &&
            (isDesktop ? (
              <Box sx={{ mb: 4, mt: 2 }}>
                <Table
                  columns={SelectLocationTableColumns({
                    handleCheck,
                    checkedId: addressId ? addressId.toString() : null,
                    t,
                  })}
                  dataSource={mapLEAddressToDataSource(
                    legalEntityStorageAddress,
                    parseDictionary(region, 'name'),
                    parseDictionary(country, 'name'),
                    parseDictionary(addressType, 'name'),
                    id as string,
                  )}
                  specificRowColor={renderRowColor}
                  specificRowColorHover={renderRowColorHover}
                />
              </Box>
            ) : (
              <CardList
                items={mapLEAddressToDataSource(
                  legalEntityStorageAddress,
                  parseDictionary(region, 'name'),
                  parseDictionary(country, 'name'),
                  parseDictionary(addressType, 'name'),
                  id as string,
                )}
                render={(data, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <SelectLocationAddressTableCard
                      data={data}
                      key={index}
                      isChecked={data.id === selectedAddress?.id}
                      onCheck={handleCheck}
                    />
                  </Box>
                )}
              />
            ))}
          {!isListNotEmpty(legalEntityStorageAddress) && canSelectOnlyExistingAddress && (
            <Box sx={{ mb: 2 }}>
              <Alert
                text={t('selectLocation.noAddressOnList.message')}
                variant={EAlertVariants.warning}
              />
            </Box>
          )}
        </Grid>
      </Grid>
    );
  } else {
    return '';
  }
};

export { SelectLocationTransactionPage };
